import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { migratePLAExpression, PLAExpressionExplanation, simulatePLAExpression, validatePLAExpression } from "./PLAExpression";
import { PLAExpressionHelpDialog } from "./PLAExpressionHelpDialog";
import { PLAExpressionInput } from "./PLAExpressionInput";
import { PLAElementRecord, PLAProject, PLARelationRecord } from "./PLAState";

export type ExpressionExplanationResult = PLAExpressionExplanation | { expression: string; error: string; warningMessages?: string[] };

export function ExpressionExplanationView(props: { explanation: ExpressionExplanationResult; showResult?: boolean }) {
   const explanation = props.explanation;
   return <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <strong>{explanation.expression}</strong>
      {"error" in explanation ? (
         <pre role="alert" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{[explanation.error, ...(explanation.warningMessages?.length ? ["", `${Localization.getText("Warning")}:`, ...explanation.warningMessages.map((message) => `  ${message}`)] : [])].join("\n")}</pre>
      ) : (
         <>
            {props.showResult && <pre role="status" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{explanation.lines[0]}</pre>}
            <details>
               <summary style={{ cursor: "pointer" }}>{Localization.getText("Details")}</summary>
               <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}>{explanation.lines.join("\n")}</pre>
            </details>
         </>
      )}
   </div>;
}

type Props = {
   project: PLAProject;
   elementTypeId: string;
   portalRoot: Element;
   onClose: () => void;
   /** Sample element the expression is evaluated from. Without it only syntax checking and completion are available. */
   element?: PLAElementRecord;
   elements?: PLAElementRecord[];
   relations?: PLARelationRecord[];
   initialExpression?: string;
   /** When provided the dialog works as an expression editor and returns a valid expression through this callback. */
   onApply?: (expression: string) => void;
};

export function PLAExpressionSimulationDialog(props: Props) {
   const [expression, setExpression] = useState(props.initialExpression || "");
   const [result, setResult] = useState<ExpressionExplanationResult>();
   const [showHelp, setShowHelp] = useState(false);
   const elements = props.elements || [];
   useEffect(() => setResult(undefined), [expression, props.element, props.project, props.elements, props.relations]);

   const valid = validatePLAExpression(expression, { project: props.project, elementTypeId: props.elementTypeId }).valid && Boolean(expression.trim());
   const migrated = useMemo(() => {
      try {
         const next = migratePLAExpression(expression);
         return next !== expression ? next : undefined;
      } catch {
         return undefined;
      }
   }, [expression]);
   const evaluate = () => {
      if (!props.element) return;
      try {
         setResult(simulatePLAExpression(expression, { element: props.element, elements, relations: props.relations || [], project: props.project }));
      } catch (error) {
         setResult({ expression, error: expression.trim() ? error instanceof Error ? error.message : String(error) : Localization.getText("Expression is required") });
      }
   };

   return createPortal(
      <AdvancedDragModal title={Localization.getText(props.onApply ? "Expression editor" : "Expression simulation")} PosDefault={{ width: "720px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={props.onClose}>
         <ADModalBody>
            <p>{Localization.getText(props.element ? "Expression simulation hint" : "Expression editor hint")}</p>
            {props.element && <p><strong>{Localization.getText("Element")}: {props.element.name || Localization.getText("Unnamed element")}</strong></p>}
            <PLAExpressionInput value={expression} onChange={setExpression} project={props.project} elementTypeId={props.elementTypeId} elements={elements} />
            {result && <div style={{ marginTop: 12 }}><ExpressionExplanationView explanation={result} showResult /></div>}
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               <GlassButton onClick={() => setShowHelp(true)}>{Localization.getText("Expression syntax help")}</GlassButton>
               {migrated && <GlassButton onClick={() => setExpression(migrated)}>{Localization.getText("Migrate expression")}</GlassButton>}
               {props.element && <GlassButton disabled={!expression.trim()} onClick={evaluate}>{Localization.getText("Evaluate expression")}</GlassButton>}
               {props.onApply && <GlassButton disabled={!valid} onClick={() => props.onApply?.(expression)}>{Localization.getText("Use expression")}</GlassButton>}
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
         {showHelp && <PLAExpressionHelpDialog onClose={() => setShowHelp(false)} />}
      </AdvancedDragModal>,
      props.portalRoot
   );
}
